/**
 * VersionCommonModal
 *
 * 공용 정보 표시 모달 컴포넌트입니다.
 * sections 배열을 props 로 전달하면 데이터 기반으로 모달 내부 레이아웃을 렌더링합니다.
 *
 * ─────────────────────────────────────────────
 * VersionCommonModal Props
 *
 *   open      {boolean}        모달 open 상태
 *   onClose   {Function}       닫기 핸들러
 *   title     {string}         모달 타이틀
 *   sections  {Section[]}      섹션 배열 (아래 구조 참고)
 *   width     {number|string}  모달 너비 (기본 460)
 *
 * ─────────────────────────────────────────────
 * Section 구조
 *
 *   {
 *     title : string,   // fieldset legend 텍스트
 *     icon  : object,   // legend 아이콘 (MUI SvgIcon 컴포넌트, 선택)
 *     items : Item[]
 *   }
 *
 * ─────────────────────────────────────────────
 * Item 타입 (type 필드로 구분)
 *
 *   type        필수 props            선택 props                      렌더링
 *   ----------  --------------------  ------------------------------  ----------------------
 *   "text"      label, value                                          label : value 텍스트
 *   "chip"      label, value          chipColor, chipVariant          label : Chip
 *   "checkbox"  label, value                                          label : 읽기전용 체크박스
 *   "chipGroup" label, items[]        items[].color                   label + Chip 묶음
 *   "custom"    render: () => Node    label (선택)                    자유 렌더링
 *
 * ─────────────────────────────────────────────
 * 새 Item 타입 추가 방법
 *
 *   1. 렌더러 컴포넌트 작성  →  const FooItem = ({ label, value }) => { ... }
 *   2. ITEM_RENDERERS 에 등록  →  foo: (item) => FooItem({ label: item.label, value: item.value })
 *   (switch 문 수정 불필요)
 *
 * ─────────────────────────────────────────────
 * 사용 예시 (ClassificationSettingsModal 참고)
 *
 *   import { VersionCommonModal } from "@plannel/components/modal/VersionCommonModal";
 *   import TuneIcon from "@mui/icons-material/Tune";
 *   import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
 *   import BarChartIcon from "@mui/icons-material/BarChart";
 *
 *   const sections = [
 *     {
 *       title: t("planInfo"),
 *       icon: TuneIcon,
 *       items: [
 *         { type: "text", label: t("classificationName"), value: classification.name },
 *         { type: "chip", label: t("status"),             value: classification.statusCd, chipColor: "success", chipVariant: "outlined" },
 *         { type: "text", label: t("classifiDimCd"),      value: classification.classifiDimCd },
 *       ],
 *     },
 *     {
 *       title: t("period"),
 *       icon: CalendarMonthIcon,
 *       items: [
 *         { type: "text", label: t("periodUom"),                          value: t(classification.periodUom) },
 *         { type: "text", label: `(${t("actual")}) ${t("numOfMonths")}`, value: classification.actualPeriodVal || "-" },
 *         { type: "text", label: `(${t("demand")}) ${t("numOfMonths")}`, value: classification.demandPeriodVal || "-" },
 *       ],
 *     },
 *     {
 *       title: t("policy"),
 *       icon: BarChartIcon,
 *       items: [
 *         { type: "text",     label: t("base"),           value: classifiObj.base },
 *         { type: "text",     label: `XYZ ${t("base")}`, value: classifiObj.xyzCalcType },
 *         { type: "checkbox", label: t("plant"),          value: classifiObj.plantLocAnalysis === true },
 *         { type: "text",     label: t("actualType"),     value: classifiObj.actualType },
 *         {
 *           type: "chipGroup",
 *           label: "ABC",
 *           items: [
 *             { label: "A", value: classifiObj.aClass, color: "#e53935" },
 *             { label: "B", value: classifiObj.bClass, color: "#fb8c00" },
 *             { label: "C", value: classifiObj.cClass, color: "#43a047" },
 *           ],
 *         },
 *         {
 *           type: "chipGroup",
 *           label: "XYZ",
 *           items: [
 *             { label: "X", value: classifiObj.xClass, color: "#1e88e5" },
 *             { label: "Y", value: classifiObj.yClass, color: "#8e24aa" },
 *             { label: "Z", value: classifiObj.zClass, color: "#546e7a" },
 *           ],
 *         },
 *       ],
 *     },
 *   ];
 *
 *   <VersionCommonModal
 *     open={open}
 *     onClose={handleClose}
 *     title={t("abcXyzClassification")}
 *     sections={sections}
 *   />
 */

import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useRef, useState, useCallback, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { isNil } from "lodash";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { ModalRule } from "@plannel/components/modal/BaseConfigModal";
import { FS } from "@plannel/utils/font-util";

// ─────────────────────────────────────────────
// 공통 스타일
// ─────────────────────────────────────────────

const fieldSetStyle = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  px: 1.5,
  pb: 0.5,
  pt: 0.5,
};

// ─────────────────────────────────────────────
// 공통 레이아웃 헬퍼
// ─────────────────────────────────────────────

const RowWrapper = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.4 }}>
    {children}
  </Box>
);

const RowLabel = ({ label }) => (
  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>
    {label}
  </Typography>
);

// ─────────────────────────────────────────────
// 아이템 타입별 렌더러 컴포넌트
// 새 타입 추가 시 여기에 컴포넌트를 작성하고
// 아래 ITEM_RENDERERS 에 한 줄만 등록하면 됩니다.
// ─────────────────────────────────────────────

const TextItem = ({ label, value }) => (
  <RowWrapper>
    <RowLabel label={label} />
    <Typography variant="body2" fontWeight={500} sx={{ textAlign: "right" }}>
      {isNil(value) || value === "" ? "-" : value}
    </Typography>
  </RowWrapper>
);

const ChipItem = ({ label, value, chipColor = "default", chipVariant = "outlined", tintColor }) => (
  <RowWrapper>
    <RowLabel label={label} />
    {isNil(value) ? (
      <Typography variant="body2" fontWeight={500}>-</Typography>
    ) : (
      // tintColor 지정 시 ABC/XYZ 칩과 동일한 채움-틴트 규격 사용 (외곽선 대신)
      tintColor
        ? <Chip label={value} size="small" sx={ColorUtil.tintChipSx(tintColor)} />
        : <Chip label={value} size="small" color={chipColor} variant={chipVariant} />
    )}
  </RowWrapper>
);

const CheckboxItem = ({ label, value }) => (
  <RowWrapper>
    <RowLabel label={label} />
    <Checkbox
      checked={value === true}
      disableRipple
      readOnly
      size="small"
      sx={{ p: 0, pointerEvents: "none" }}
    />
  </RowWrapper>
);

const ChipGroupItem = ({ label, items = [] }) => (
  <RowWrapper>
    {label && <RowLabel label={label} />}
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {items
        .filter(({ value }) => !isNil(value))
        .map(({ label: chipLabel, value: chipValue, color }) => (
          <Chip
            key={chipLabel}
            label={`${chipLabel}: ${chipValue}%`}
            size="small"
            sx={ColorUtil.tintChipSx(color)}
          />
        ))}
    </Box>
  </RowWrapper>
);

const CustomItem = ({ render }) => (
  <Box>{render?.()}</Box>
);

// ─────────────────────────────────────────────
// 아이템 렌더러 레지스트리
//
// 새 타입 추가 방법:
//   1. 위에 렌더러 컴포넌트 작성
//   2. 여기에 한 줄 추가: newType: (item) => <NewTypeItem {...item} />
// ─────────────────────────────────────────────

const ITEM_RENDERERS = {
  text: (item) => <TextItem label={item.label} value={item.value} />,
  chip: (item) => <ChipItem label={item.label} value={item.value} chipColor={item.chipColor} chipVariant={item.chipVariant} tintColor={item.tintColor} />,
  checkbox: (item) => <CheckboxItem label={item.label} value={item.value} />,
  chipGroup: (item) => <ChipGroupItem label={item.label} items={item.items} />,
  custom: (item) => <CustomItem render={item.render} />,
};

const renderItem = (item, index) => {
  const renderer = ITEM_RENDERERS[item.type];
  if (!renderer) {
    debug(`지원하지 않는 Component Item Renderer 입니다.: "${item.type}"`);
    return null;
  }
  return <Box key={`${item.type}-${index}`}>{renderer(item)}</Box>;
};

// ─────────────────────────────────────────────
// 섹션 컴포넌트
// ─────────────────────────────────────────────

const ModalInfoSection = ({ title, icon: Icon, items = [] }) => (
  <Box component="fieldset" sx={fieldSetStyle}>
    <Box component="legend" sx={{ px: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
      {Icon && <Icon sx={{ fontSize: 14 }} />}
      <Typography variant="caption" fontWeight={600}>{title}</Typography>
    </Box>
    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
      {items.map((item, index) => (
        <Box key={index}>
          {renderItem(item, index)}
          {index < items.length - 1 && item.type !== "custom" && (
            <Divider />
          )}
        </Box>
      ))}
    </Stack>
  </Box>
);

// ─────────────────────────────────────────────
// ModalInfoLayout: sections 렌더링 (내부 전용)
// ─────────────────────────────────────────────

const ModalInfoLayout = ({ sections = [] }) => (
  <Stack spacing={2}>
    {sections
      .filter((section) => !isNil(section))
      .map((section, index) => (
        <ModalInfoSection
          key={index}
          title={section.title}
          icon={section.icon}
          items={section.items ?? []}
        />
      ))}
  </Stack>
);

// ─────────────────────────────────────────────
// VersionCommonModal 내부 베이스 컴포넌트
// (withTranslation HOC 래핑 후 VersionCommonModal 로 export)
// ─────────────────────────────────────────────

const VersionCommonModalBase = ({ open, onClose, title, sections, t, width = 460 }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const dragStart = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });
  const dragCleanup = useRef(null);

  // 컴포넌트 unmount 시 등록된 드래그 리스너 강제 제거
  useEffect(() => {
    return () => {
      dragCleanup.current?.();
    };
  }, []);

  const handleClose = () => {
    setPosition({ x: 0, y: 0 });
    onClose?.();
  };

  const onTitleMouseDown = useCallback((e) => {
    dragging.current = true;
    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      posX: position.x,
      posY: position.y,
    };

    const onMouseMove = (mv) => {
      if (!dragging.current) return;
      setPosition({
        x: dragStart.current.posX + mv.clientX - dragStart.current.mouseX,
        y: dragStart.current.posY + mv.clientY - dragStart.current.mouseY,
      });
    };

    const onMouseUp = () => {
      dragging.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      dragCleanup.current = null;
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    // unmount 대비: cleanup 함수를 ref에 저장
    dragCleanup.current = () => {
      dragging.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [position]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          width,
          minWidth: 320,
          maxHeight: "90vh",
          overflow: "auto",
          resize: "both",
          bgcolor: "background.paper",
          borderRadius: 2, // BaseConfigModal 과 동일
          boxShadow: 24,
          outline: "none",
        }}
      >
        {/* 드래그 핸들 = 제목 영역. 셸로 이관하지 않는 이유가 이것 — Dialog 로 옮기면 드래그를 잃는다.
            제목/구분선 규격은 BaseConfigModal 과 같다(20/700 + full-bleed 1px 헤어라인 상·하). */}
        <Box
          onMouseDown={onTitleMouseDown}
          sx={{ cursor: "move", userSelect: "none", pt: 2, px: 2, pb: 1 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <DragIndicatorIcon sx={{ fontSize: 18, color: "text.disabled" }} />
            <Typography component="h2" sx={{ flexGrow: 1, fontSize: FS.DISPLAY, fontWeight: 700 }}>
              {title}
            </Typography>
          </Box>
        </Box>
        <ModalRule />
        <Box sx={{ p: 2, pt: 2 }}>
          <ModalInfoLayout sections={sections} />
        </Box>
        <ModalRule />
        {/* 읽기 전용이라 저장이 없다 — 셸이 onSave 없이 렌더할 때와 같은 모양(우측 정렬 텍스트 버튼 하나).
            라벨은 close 유지(취소할 것이 없다). */}
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
          <Button size="small" startIcon={<CloseIcon />} onClick={handleClose}>
            {t("close")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const VersionCommonModal = withTranslation()(VersionCommonModalBase);

export { VersionCommonModal };
